/**
 * O cartão dos recebimentos do conector, em Integrações → Carga Máquina desde a 11.2 (FR-72,
 * FR-61-A).
 *
 * O conector que a Silcom instala junto ao Carga Máquina empurra para o CRM cada Orçamento
 * exportado e, desde a 5.4, cada Pedido liberado. Este cartão é o **único** lugar onde alguém vê o
 * que chegou — e, quando algo deu errado, por quê. Sem ele, "um Orçamento se perde em silêncio"
 * continuaria verdade: o recebimento estaria gravado no banco e ninguém saberia olhar.
 *
 * **Os dois tipos convivem na mesma lista, e a coluna do identificador é quem os separa.** Ela
 * nomeia o tipo e mostra o identificador *dele* — "Orçamento ORC-412", "Pedido PED-412". Uma lista
 * só, e não duas: a pergunta é a mesma nos dois casos ("o que não passou?"), e duas telas dariam
 * dois lugares para olhar quando o conector parasse. E não há filtro por tipo pelo mesmo motivo: o
 * filtro que existe é o de **situação**, que é a pergunta que traz alguém aqui.
 *
 * **Ele é só para a gestão, e aqui a exceção à convenção do repositório é do serviço.** Desde a 2.1
 * a regra é "esconder não é autorizar": as telas mostram as ações e o serviço recusa. Aqui as duas
 * rotas respondem `403` a Vendedor e SDR — a **leitura** inclusive —, então um cartão montado para
 * eles mostraria uma lista permanentemente vazia com uma mensagem de erro embaixo. É o mesmo
 * `ehDeGestao` do shell que decide o item "Administração" do menu, e é ele que decide aqui: duas
 * leituras diferentes fariam a tela e o serviço discordarem sobre quem pode.
 *
 * **O filtro por situação é o que a tela existe para exercer.** O desfecho de um recebimento é
 * `recebido`, `em_processamento`, `aplicado` ou `rejeitado`, e a pergunta que traz alguém a esta
 * tela é sempre a mesma: "o que não passou?". O filtro nasce em **Rejeitados** por isso — abrir em
 * "Todos" faria a lista mais útil ser a segunda tela, e num sistema exportando de verdade os
 * aplicados afogam os rejeitados.
 *
 * **O motivo é visível, e não um detalhe atrás de um clique.** Ele é a única coisa que distingue
 * "esse número nunca existiu aqui" de "o Negócio já estava perdido" — e é ele que diz a quem olha
 * se o conserto é criar o Negócio, corrigir o código no ERP, ou nada.
 *
 * **Reprocessar não reenvia nada.** O serviço refaz o processamento sobre o payload que já está
 * guardado, e é por isso que o botão resolve o caso real: o Orçamento chegou antes de o Negócio
 * existir, alguém criou o Negócio, e agora o código casa. Ele aparece nas **duas** situações que o
 * serviço aceita — `rejeitado` e `recebido` —, e não nas outras duas, que respondem `409`: um botão
 * que só sabe falhar é interface para o que não existe. O `recebido` é o preço declarado do AD-7
 * (a linha que ficou para trás quando o processo morreu entre os dois commits), e sem o botão nela
 * ela seria o único estado do sistema sem caminho de saída.
 *
 * **A lista é paginada, e a paginação não é enfeite.** Ela só cresce: com o conector exportando de
 * verdade, o recebimento rejeitado mais antigo sairia da primeira janela do serviço e ficaria
 * invisível dentro da tela que existe para mostrá-lo — o Orçamento perdido sumindo pela segunda
 * vez, agora dentro do CRM. Sem um total no contrato, "há mais" é deduzido da **página cheia**, que
 * é a dedução honesta possível: página cheia pode ter mais, página incompleta não tem.
 *
 * **O desfecho pode mudar sem a tela saber.** Reprocessar devolve `em_processamento`, e o desfecho
 * final chega depois, por evento, quando o `negocios` responde. A tela não fica esperando: ela
 * mostra o que o serviço tem agora e diz, na confirmação, que o resultado aparece na próxima
 * leitura. Um `setInterval` aqui seria um segundo relógio para um fato que já tem o seu.
 *
 * **Paridade de celular (NFR-1).** Duas árvores pelo `useEhCelular` do shell, e nenhuma `@media`:
 * no computador é a tabela de cinco colunas, no celular são cartões empilhados com a **mesma**
 * informação e a mesma ação. É o molde de `Tarefas.tsx` e de `ListaDeEmpresas.tsx`.
 */

import { useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { ehDeGestao } from '@/shell/navegacao'
import { useEhCelular } from '@/shell/Shell'
import {
  LIMITE_DOS_RECEBIMENTOS,
  type RecebimentoDoConector,
  type SituacaoDoRecebimento,
  useRecebimentosDoConector,
  useReprocessarRecebimento,
} from './api'
import './recebimentos-do-conector.css'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'recebimentos-do-conector-titulo'
const ID_DO_FILTRO = 'recebimentos-do-conector-filtro'

const CARREGANDO = 'Carregando os recebimentos…'
const VAZIO = 'Nenhum recebimento com esta situação.'
const REPROCESSAR = 'Reprocessar'
const REPROCESSANDO = 'Reprocessando…'
const ANTERIOR = 'Anterior'
const PROXIMA = 'Próxima'
const PAGINA_VAZIA = 'Esta página ficou vazia.'
const VOLTAR_AO_INICIO = 'Voltar ao início'
const REPROCESSADO =
  'Reprocessado. O desfecho final aparece na próxima leitura, quando o Negócios responder.'

/**
 * O rótulo de cada desfecho, e a ordem em que eles aparecem no filtro.
 *
 * "Todos" é a chave `undefined` — ausência de filtro, e não um quinto valor. Os quatro nomes são a
 * tradução dos valores do serviço: a coluna guarda `em_processamento`, e "Em processamento" é o que
 * alguém lê. Um `replace('_', ' ')` daria "Em processamento" por acaso e "Recebido" por sorte.
 */
const ROTULOS: Record<SituacaoDoRecebimento, string> = {
  rejeitado: 'Rejeitados',
  em_processamento: 'Em processamento',
  recebido: 'Recebidos',
  aplicado: 'Aplicados',
}

/** O rótulo de uma linha, no singular — a coluna "Situação" da tabela. */
const ROTULOS_DA_LINHA: Record<SituacaoDoRecebimento, string> = {
  rejeitado: 'Rejeitado',
  em_processamento: 'Em processamento',
  recebido: 'Recebido',
  aplicado: 'Aplicado',
}

/**
 * As opções do filtro, na ordem em que a tela as oferece.
 *
 * **Rejeitados vem primeiro, e é o padrão** — veja o cabeçalho: a pergunta que traz alguém aqui é
 * "o que não passou?".
 */
const FILTROS: { valor: SituacaoDoRecebimento | 'todas'; rotulo: string }[] = [
  { valor: 'rejeitado', rotulo: ROTULOS.rejeitado },
  { valor: 'em_processamento', rotulo: ROTULOS.em_processamento },
  { valor: 'recebido', rotulo: ROTULOS.recebido },
  { valor: 'aplicado', rotulo: ROTULOS.aplicado },
  { valor: 'todas', rotulo: 'Todos' },
]

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O rótulo de uma situação que o serviço mandou.
 *
 * O valor cru é a saída quando ele não está no mapa: o contrato declara `situacao` como texto, e um
 * quinto estado inventado amanhã apareceria como ele é, em vez de sumir da tela — que é o que um
 * `?? ''` faria.
 */
function rotuloDaSituacao(situacao: string): string {
  return ROTULOS_DA_LINHA[situacao as SituacaoDoRecebimento] ?? situacao
}

/** O que a tela precisa saber sobre um tipo de recebimento: como chamá-lo e o que o identifica. */
type DescricaoDoTipo = {
  /** O rótulo que a tela mostra — com acento e maiúscula, como o Glossário escreve. */
  readonly rotulo: string
  /** O campo daquele tipo, lido da linha: o serviço só preenche o do tipo dela. */
  readonly identificador: (recebimento: RecebimentoDoConector) => string | null
}

/**
 * Os tipos de recebimento (5.4), com o rótulo e o identificador de cada um **num lugar só**.
 *
 * **É um mapa, e não um `if` sobre o literal `'pedido'`**, pela mesma razão pela qual o serviço
 * mantém três mapas exaustivos com `_conferir_os_tipos()` no import: um `if` tem `else`, e o `else`
 * é onde um terceiro tipo cairia — lendo o campo do Orçamento e mostrando o identificador errado,
 * enquanto o rótulo, esse sim vindo de um mapa, apareceria correto. Um tipo desconhecido aqui não
 * tem ramo nenhum para cair: o rótulo sai cru e o identificador sai `null`, que é o travessão.
 *
 * Os dois valores do discriminador são nomeados **uma vez**, aqui: as chaves deste objeto são a
 * única lista deles no SPA.
 */
const TIPOS_DE_RECEBIMENTO: Record<string, DescricaoDoTipo> = {
  orcamento: { rotulo: 'Orçamento', identificador: (recebimento) => recebimento.orcamento_erp_id },
  pedido: { rotulo: 'Pedido', identificador: (recebimento) => recebimento.numero_do_pedido },
}

/**
 * O rótulo de um tipo que o serviço mandou — com o valor cru como saída.
 *
 * Mesma decisão de `rotuloDaSituacao`: o contrato declara `tipo` como texto, e um terceiro tipo
 * inventado amanhã aparece como ele é em vez de sumir da tela.
 */
function rotuloDoTipo(tipo: string): string {
  return TIPOS_DE_RECEBIMENTO[tipo]?.rotulo ?? tipo
}

/**
 * O identificador daquele recebimento — o do **seu** tipo.
 *
 * O serviço manda os dois campos e preenche só o do tipo da linha; ler o do outro daria o traço em
 * metade da lista sem dizer por quê. A escolha é feita pelo `tipo`, e não por "o que não for nulo":
 * um payload restaurado de um ponto anterior pode não ter a chave, e nesse caso o traço é a
 * resposta honesta — e não o identificador do outro tipo, que não existe naquela linha.
 */
function identificadorDe(recebimento: RecebimentoDoConector): string | null {
  return TIPOS_DE_RECEBIMENTO[recebimento.tipo]?.identificador(recebimento) ?? null
}

/**
 * O conteúdo da célula do identificador: o **tipo** e o identificador dele (5.4).
 *
 * Ele é um componente, e não dois trechos parecidos, pela razão de sempre: a tabela e o cartão de
 * celular mostram a mesma coisa (NFR-1), e duas cópias divergiriam no primeiro tipo novo.
 *
 * **Ele não traz classe própria de bloco**, e a ausência é deliberada: a tabela e o cartão têm
 * tipografias diferentes, e um `<span>` com a classe do cartão dentro da `<td>` encolhia os
 * identificadores da tabela. Quem embrulha — e escolhe a tipografia — é cada árvore.
 */
function IdentificadorDaLinha({ recebimento }: { recebimento: RecebimentoDoConector }) {
  return (
    <>
      <span className="recebimentos-erp__tipo">{rotuloDoTipo(recebimento.tipo)}</span>{' '}
      {identificadorDe(recebimento) ?? '—'}
    </>
  )
}

/** Data e hora curtas, no fuso de quem está olhando. */
function quando(instante: string): string {
  return new Date(instante).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * As duas situações que o serviço aceita reprocessar.
 *
 * `rejeitado` é o caso conhecido. `recebido` é o preço declarado do AD-7: a gravação é commitada
 * **antes** do processamento, e entre os dois commits o processo pode morrer — a linha fica ali,
 * gravada e visível, sem nunca ter sido olhada. Sem o botão nela, ela seria o único estado do
 * sistema sem caminho de saída, e a tela mostraria um recebimento que ninguém consegue terminar.
 *
 * As outras duas respondem `409`, e a lista é lida do serviço: `MENSAGEM_DE_RECEBIMENTO_QUE_NAO_
 * REPROCESSA` diz a mesma regra do outro lado.
 */
const REPROCESSAVEIS: readonly string[] = ['rejeitado', 'recebido']

/**
 * O botão que reprocessa.
 *
 * Ele existe **só nas linhas que o serviço aceita**: `aplicado` e `em_processamento` respondem
 * `409`, e um botão que só sabe falhar é interface para o que não existe.
 *
 * O nome acessível carrega o **código do Negócio**: numa lista de vinte linhas, vinte botões
 * chamados "Reprocessar" são indistinguíveis para quem navega por leitor de tela. É o molde de
 * `Tarefas.tsx`.
 */
function AcaoDaLinha({
  recebimento,
  emVoo,
  aoReprocessar,
}: {
  recebimento: RecebimentoDoConector
  emVoo: boolean
  aoReprocessar: (recebimento: RecebimentoDoConector) => void
}) {
  if (!REPROCESSAVEIS.includes(recebimento.situacao)) {
    return null
  }
  return (
    <button
      type="button"
      className="recebimentos-erp__acao"
      aria-label={`${REPROCESSAR}: Negócio ${String(recebimento.codigo_do_negocio)}`}
      disabled={emVoo}
      onClick={() => {
        aoReprocessar(recebimento)
      }}
    >
      {emVoo ? REPROCESSANDO : REPROCESSAR}
    </button>
  )
}

/** O selo da situação — cor por `data-situacao`, no CSS, e nunca por estilo inline. */
function SeloDaSituacao({ situacao }: { situacao: string }) {
  return (
    <span className="recebimentos-erp__selo" data-situacao={situacao}>
      {rotuloDaSituacao(situacao)}
    </span>
  )
}

/**
 * O motivo da recusa.
 *
 * Ele é `null` em tudo que não foi recusado, e ali a célula fica com o travessão: uma célula vazia
 * e uma célula "sem motivo" são a mesma coisa na tela, e o travessão diz que ninguém esqueceu de
 * preencher.
 */
function MotivoDaLinha({ motivo }: { motivo: string | null }) {
  if (motivo === null) {
    return <span className="recebimentos-erp__sem-motivo">—</span>
  }
  return <span className="recebimentos-erp__motivo">{motivo}</span>
}

/** Uma linha da tabela do computador. */
function LinhaDaTabela({
  recebimento,
  emVoo,
  aoReprocessar,
}: {
  recebimento: RecebimentoDoConector
  emVoo: boolean
  aoReprocessar: (recebimento: RecebimentoDoConector) => void
}) {
  return (
    <tr className="recebimentos-erp__linha" aria-busy={emVoo || undefined}>
      <td className="recebimentos-erp__celula recebimentos-erp__celula--codigo">
        {recebimento.codigo_do_negocio}
      </td>
      <td className="recebimentos-erp__celula">
        <IdentificadorDaLinha recebimento={recebimento} />
      </td>
      <td className="recebimentos-erp__celula">
        <SeloDaSituacao situacao={recebimento.situacao} />
      </td>
      <td className="recebimentos-erp__celula">
        <MotivoDaLinha motivo={recebimento.motivo} />
      </td>
      <td className="recebimentos-erp__celula recebimentos-erp__celula--quando">
        {quando(recebimento.recebido_em)}
      </td>
      <td className="recebimentos-erp__celula recebimentos-erp__celula--acao">
        <AcaoDaLinha recebimento={recebimento} emVoo={emVoo} aoReprocessar={aoReprocessar} />
      </td>
    </tr>
  )
}

/** Um cartão empilhado do celular — a mesma informação e a mesma ação (NFR-1). */
function CartaoDoRecebimento({
  recebimento,
  emVoo,
  aoReprocessar,
}: {
  recebimento: RecebimentoDoConector
  emVoo: boolean
  aoReprocessar: (recebimento: RecebimentoDoConector) => void
}) {
  return (
    <li className="recebimentos-erp__cartao-linha" aria-busy={emVoo || undefined}>
      <div className="recebimentos-erp__cartao-topo">
        <span className="recebimentos-erp__codigo">Negócio {recebimento.codigo_do_negocio}</span>
        <SeloDaSituacao situacao={recebimento.situacao} />
      </div>
      <span className="recebimentos-erp__identificador">
        <IdentificadorDaLinha recebimento={recebimento} />
      </span>
      <MotivoDaLinha motivo={recebimento.motivo} />
      <div className="recebimentos-erp__cartao-rodape">
        <span className="recebimentos-erp__quando">{quando(recebimento.recebido_em)}</span>
        <AcaoDaLinha recebimento={recebimento} emVoo={emVoo} aoReprocessar={aoReprocessar} />
      </div>
    </li>
  )
}

export function RecebimentosDoConector() {
  const { data: sessao } = useSessao()
  const ehCelular = useEhCelular()
  /*
   * O filtro nasce em "Rejeitados" — veja o cabeçalho. Ele é estado local, e não query da URL: a
   * tela de Administração tem cinco cartões e nenhum deles põe estado na URL; o primeiro a fazê-lo
   * decidiria, sozinho, que a tela inteira passa a ter estado compartilhável.
   */
  const [filtro, setFiltro] = useState<SituacaoDoRecebimento | 'todas'>('rejeitado')
  /*
   * A página é 1-based e mora no estado, como o filtro. A lista **só cresce**: com o conector
   * exportando de verdade, o recebimento rejeitado mais antigo sairia da primeira página, e sem
   * paginação ele ficaria invisível dentro da tela que existe para mostrá-lo — o Orçamento perdido
   * some pela segunda vez, agora dentro do CRM.
   */
  const [pagina, setPagina] = useState(1)

  const podeVer = sessao !== undefined && ehDeGestao(sessao.escopo_de_carteira)
  const situacao = filtro === 'todas' ? undefined : filtro
  const deslocamento = (pagina - 1) * LIMITE_DOS_RECEBIMENTOS
  /*
   * `enabled` fecha a porta antes da rede: sem sessão de gestão a consulta nem sai, e a tela não
   * pisca um erro que ela já sabe que viria. A guarda de rota não deixa nada interno renderizar sem
   * sessão, e este ramo é a defesa contra o estado que ela já impede.
   */
  const consulta = useRecebimentosDoConector(situacao, { habilitada: podeVer, deslocamento })
  const reprocesso = useReprocessarRecebimento()

  const itens = consulta.data?.itens ?? []
  /*
   * **"Há mais" é deduzido da página cheia**, e não de um total que o serviço não devolve. É a
   * dedução honesta possível com este contrato: página cheia pode ter mais, página incompleta não
   * tem. O preço é uma "Próxima" que às vezes leva a uma página vazia — e o efeito abaixo cobre
   * exatamente esse caso, voltando sozinho.
   */
  const haMais = itens.length === LIMITE_DOS_RECEBIMENTOS

  /*
   * A página ficou além do fim: o filtro mudou o conjunto, ou o reprocesso tirou daqui a última
   * linha que ela tinha. Sem nomear esse estado, a tela diria "Nenhum recebimento com esta
   * situação." sobre uma lista que **tem** linhas — a tela se contradizendo em uma frase.
   *
   * A correção é **um botão**, e não um `useEffect` que chame `setPagina`. Um salto automático
   * levaria a pessoa para outro lugar enquanto ela olha, e o efeito ainda dispararia sobre o dado
   * de uma consulta que pode estar em voo. `ListaDeEmpresas` corrige por efeito porque a página
   * dela mora na **URL** (a correção é uma navegação, e o histórico registra); aqui ela é estado
   * local, e o clique é o que torna a correção visível a quem a pede.
   */
  const alemDoFim = consulta.data !== undefined && itens.length === 0 && pagina > 1

  if (!podeVer) {
    /*
     * Nem um cartão vazio, nem uma mensagem de "você não pode": as duas rotas respondem `403` a
     * Vendedor e SDR, então não há nada a mostrar e não há nada que eles possam fazer aqui. É a
     * mesma decisão do item "Administração" no menu, e ela está explicada no cabeçalho.
     */
    return null
  }

  function reprocessar(recebimento: RecebimentoDoConector) {
    // A mutação é zerada a cada envio: a confirmação e a recusa falam do envio que está em voo, e
    // nunca do anterior.
    reprocesso.reset()
    reprocesso.mutate(recebimento.id)
  }

  return (
    <section className="recebimentos-erp" aria-labelledby={ID_DO_TITULO}>
      <div className="recebimentos-erp__cartao">
        <div className="recebimentos-erp__cabecalho">
          <h2 className="recebimentos-erp__titulo" id={ID_DO_TITULO}>
            Recebimentos do ERP
          </h2>
          <span className="recebimentos-erp__ajuda">
            Cada Orçamento que o Carga Máquina exportou para o CRM, com o desfecho. O rejeitado pode
            ser reprocessado a partir do que já chegou — nada é reenviado pelo ERP.
          </span>
          <div className="recebimentos-erp__filtro">
            <label className="recebimentos-erp__rotulo" htmlFor={ID_DO_FILTRO}>
              Situação
            </label>
            <select
              id={ID_DO_FILTRO}
              className="recebimentos-erp__seletor"
              value={filtro}
              onChange={(evento) => {
                reprocesso.reset()
                // Trocar o filtro volta para a primeira página: a página 3 de "Rejeitados" não é
                // a página 3 de "Todos", e manter o número mostraria uma janela do meio de outro
                // conjunto — ou uma tela vazia sobre uma lista que tem linhas.
                setPagina(1)
                setFiltro(evento.target.value as SituacaoDoRecebimento | 'todas')
              }}
            >
              {FILTROS.map((opcao) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="recebimentos-erp__corpo">
          {consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <p className="recebimentos-erp__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : /* `|| consulta.data === undefined` fecha o terceiro estado: sem rede, a consulta fica
                **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela desenharia uma
                lista vazia como se não houvesse recebimento nenhum. */
          consulta.isLoading || consulta.data === undefined ? (
            <p className="recebimentos-erp__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : alemDoFim ? (
            <p className="recebimentos-erp__ajuda" role="status">
              {PAGINA_VAZIA}{' '}
              <button
                type="button"
                className="recebimentos-erp__pagina"
                onClick={() => {
                  setPagina(1)
                }}
              >
                {VOLTAR_AO_INICIO}
              </button>
            </p>
          ) : itens.length === 0 ? (
            <p className="recebimentos-erp__ajuda" role="status">
              {VAZIO}
            </p>
          ) : ehCelular ? (
            <ul className="recebimentos-erp__cartoes">
              {itens.map((recebimento) => (
                <CartaoDoRecebimento
                  key={recebimento.id}
                  recebimento={recebimento}
                  emVoo={reprocesso.isPending && reprocesso.variables === recebimento.id}
                  aoReprocessar={reprocessar}
                />
              ))}
            </ul>
          ) : (
            <table className="recebimentos-erp__tabela">
              <thead>
                <tr>
                  <th scope="col">Negócio</th>
                  {/* "Identificador", e não "Orçamento": desde a 5.4 a coluna carrega os dois
                      tipos, e o nome do tipo vem dentro da célula, por linha. */}
                  <th scope="col">Identificador</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Motivo</th>
                  <th scope="col">Recebido</th>
                  <th scope="col">
                    <span className="recebimentos-erp__so-para-leitor">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {itens.map((recebimento) => (
                  <LinhaDaTabela
                    key={recebimento.id}
                    recebimento={recebimento}
                    emVoo={reprocesso.isPending && reprocesso.variables === recebimento.id}
                    aoReprocessar={reprocessar}
                  />
                ))}
              </tbody>
            </table>
          )}

          {/* A navegação só aparece quando há para onde ir: numa instalação com meia dúzia de
              recebimentos, dois botões desabilitados seriam interface para o que não existe. */}
          {pagina > 1 || haMais ? (
            <nav className="recebimentos-erp__paginacao" aria-label="Paginação">
              <button
                type="button"
                className="recebimentos-erp__pagina"
                disabled={pagina === 1}
                onClick={() => {
                  reprocesso.reset()
                  setPagina((atual) => Math.max(1, atual - 1))
                }}
              >
                {ANTERIOR}
              </button>
              {/* A posição é declarada, e não deduzida do que está na tela: sem um total, "Página
                  2" é a única coisa honesta que se pode dizer — e é ela que avisa que existe mais
                  do que a primeira janela. */}
              <span className="recebimentos-erp__posicao" role="status">
                Página {pagina}
              </span>
              <button
                type="button"
                className="recebimentos-erp__pagina"
                disabled={!haMais}
                onClick={() => {
                  reprocesso.reset()
                  setPagina((atual) => atual + 1)
                }}
              >
                {PROXIMA}
              </button>
            </nav>
          ) : null}

          {/* A recusa e a confirmação do reprocesso ficam fora da lista: elas falam do ato, e não
              da linha — que pode ter saído do filtro corrente depois de mudar de situação.
              Verbatim: a recusa do serviço é a que sabe explicar o caso. */}
          {reprocesso.isError ? (
            <p className="recebimentos-erp__erro" role="alert">
              {mensagemDe(reprocesso.error)}
            </p>
          ) : null}
          {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
              interromper o que a pessoa estiver fazendo para dizer isso. */}
          {reprocesso.isSuccess ? (
            <p className="recebimentos-erp__sucesso" role="status">
              {REPROCESSADO}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  )
}
